import React, { useEffect, useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Shield, User } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const EMPTY = {
  name: "", company: "", url: "", username: "", email: "", secret: "",
  category_id: "", group_id: "", asset_type: "Outros", provider: "",
  monthly_value: 0, renewal_date: "", expiration_date: "", notes: "",
  responsible: "", permissions: [],
};

export default function CredentialFormDialog({ open, onOpenChange, editing, categories, groups, assetTypes, onSaved }) {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");

  useEffect(() => {
    if (open && isAdmin) {
      api.get("/users").then(({ data }) => setUsers(data.filter((u) => u.role !== "admin")));
    }
  }, [open, isAdmin]);

  useEffect(() => {
    if (open) {
      if (editing) {
        setForm({
          name: editing.name || "",
          company: editing.company || "",
          url: editing.url || "",
          username: editing.username || "",
          email: editing.email || "",
          secret: "",
          category_id: editing.category_id || "",
          group_id: editing.group_id || "",
          asset_type: editing.asset_type || "Outros",
          provider: editing.provider || "",
          monthly_value: editing.monthly_value || 0,
          renewal_date: editing.renewal_date ? editing.renewal_date.slice(0, 10) : "",
          expiration_date: editing.expiration_date ? editing.expiration_date.slice(0, 10) : "",
          notes: editing.notes || "",
          responsible: editing.responsible || "",
          permissions: editing.permissions || [],
        });
      } else {
        setForm(EMPTY);
      }
      setUserSearch("");
    }
  }, [open, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const permMap = useMemo(() => {
    const m = {};
    for (const p of form.permissions) m[p.user_id] = p;
    return m;
  }, [form.permissions]);

  const togglePermission = (userId, field) => {
    const cur = permMap[userId];
    let next;
    if (!cur) {
      next = { user_id: userId, can_view: true, can_view_secret: false, can_edit: false, [field]: true };
    } else {
      next = { ...cur, [field]: !cur[field] };
      // Edit and view_secret require can_view
      if (field === "can_view" && !next.can_view) {
        next.can_view_secret = false;
        next.can_edit = false;
      }
      if ((field === "can_view_secret" || field === "can_edit") && next[field]) {
        next.can_view = true;
      }
    }
    const others = form.permissions.filter((p) => p.user_id !== userId);
    // Remove fully empty entries
    if (!next.can_view && !next.can_view_secret && !next.can_edit) {
      setForm((f) => ({ ...f, permissions: others }));
    } else {
      setForm((f) => ({ ...f, permissions: [...others, next] }));
    }
  };

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    );
  }, [users, userSearch]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        category_id: form.category_id || null,
        group_id: form.group_id || null,
        renewal_date: form.renewal_date || null,
        expiration_date: form.expiration_date || null,
        monthly_value: Number(form.monthly_value) || 0,
      };
      if (editing) {
        if (!form.secret) delete payload.secret;
        await api.put(`/credentials/${editing.id}`, payload);
        toast.success("Credencial atualizada");
      } else {
        await api.post("/credentials", payload);
        toast.success("Credencial criada");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err, "Erro ao salvar credencial"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="credential-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {editing ? "Editar credencial" : "Nova credencial"}
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            Preencha os dados do ativo digital. A senha/token será criptografada antes de salvar.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Tabs defaultValue="basic">
            <TabsList className="w-full grid grid-cols-2" data-testid="cred-form-tabs">
              <TabsTrigger value="basic" data-testid="tab-basic">Dados gerais</TabsTrigger>
              <TabsTrigger value="access" data-testid="tab-access" disabled={!isAdmin}>
                <Shield className="w-3.5 h-3.5 mr-1.5" />
                Acesso{isAdmin ? "" : " (admin)"}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="cred-name">Nome da credencial *</Label>
                  <Input id="cred-name" data-testid="cred-input-name" value={form.name} onChange={(e) => set("name", e.target.value)} required />
                </div>
                <div>
                  <Label htmlFor="cred-company">Empresa</Label>
                  <Input id="cred-company" data-testid="cred-input-company" value={form.company} onChange={(e) => set("company", e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="cred-url">Site / URL</Label>
                  <Input id="cred-url" data-testid="cred-input-url" value={form.url} onChange={(e) => set("url", e.target.value)} placeholder="https://" />
                </div>
                <div>
                  <Label htmlFor="cred-username">Usuário</Label>
                  <Input id="cred-username" data-testid="cred-input-username" value={form.username} onChange={(e) => set("username", e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="cred-email">E-mail</Label>
                  <Input id="cred-email" data-testid="cred-input-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="cred-secret">Senha / Token {editing && <span className="text-xs text-gray-500">(deixe em branco para manter)</span>}</Label>
                  <Input id="cred-secret" data-testid="cred-input-secret" value={form.secret} onChange={(e) => set("secret", e.target.value)} placeholder={editing ? "Não alterar" : ""} />
                </div>
                <div>
                  <Label>Categoria</Label>
                  <Select value={form.category_id || "_none"} onValueChange={(v) => set("category_id", v === "_none" ? "" : v)}>
                    <SelectTrigger data-testid="cred-select-category"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">— Sem categoria —</SelectItem>
                      {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Cofre</Label>
                  <Select value={form.group_id || "_none"} onValueChange={(v) => set("group_id", v === "_none" ? "" : v)} disabled={!isAdmin}>
                    <SelectTrigger data-testid="cred-select-group"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">— Sem cofre —</SelectItem>
                      {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tipo do ativo</Label>
                  <Select value={form.asset_type} onValueChange={(v) => set("asset_type", v)}>
                    <SelectTrigger data-testid="cred-select-asset-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {assetTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="cred-provider">Fornecedor</Label>
                  <Input id="cred-provider" data-testid="cred-input-provider" value={form.provider} onChange={(e) => set("provider", e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="cred-monthly">Valor mensal (R$)</Label>
                  <Input id="cred-monthly" data-testid="cred-input-monthly" type="number" step="0.01" value={form.monthly_value} onChange={(e) => set("monthly_value", e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="cred-renewal">Data de renovação</Label>
                  <Input id="cred-renewal" data-testid="cred-input-renewal" type="date" value={form.renewal_date} onChange={(e) => set("renewal_date", e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="cred-expiration">Data de expiração</Label>
                  <Input id="cred-expiration" data-testid="cred-input-expiration" type="date" value={form.expiration_date} onChange={(e) => set("expiration_date", e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="cred-responsible">Responsável</Label>
                  <Input id="cred-responsible" data-testid="cred-input-responsible" value={form.responsible} onChange={(e) => set("responsible", e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="cred-notes">Observações</Label>
                  <Textarea id="cred-notes" data-testid="cred-input-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="access" className="pt-4 space-y-4">
              <div className="bg-orange-50 border border-orange-100 rounded-lg p-3 text-sm text-orange-900">
                <strong>Como funciona o acesso:</strong>
                <ul className="mt-1 list-disc list-inside space-y-0.5 text-xs">
                  <li>Administradores sempre visualizam tudo.</li>
                  <li>Se a credencial estiver em um cofre, membros desse cofre veem a credencial e a senha (sem permissão para editar).</li>
                  <li>Permissões individuais abaixo <strong>sobrescrevem</strong> as do cofre — você pode liberar mais ou restringir.</li>
                  <li>Sem cofre e sem permissões individuais: visível apenas para administradores.</li>
                </ul>
              </div>

              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Buscar membro por nome ou e-mail..."
                  className="pl-9"
                  data-testid="cred-perm-search"
                />
              </div>

              <div className="border border-gray-200 rounded-lg max-h-80 overflow-y-auto">
                {filteredUsers.length === 0 ? (
                  <div className="text-sm text-gray-500 p-4 text-center">Nenhum membro encontrado</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-gray-500 font-semibold">Membro</th>
                        <th className="text-center py-2 px-2 text-xs uppercase tracking-wider text-gray-500 font-semibold">Ver</th>
                        <th className="text-center py-2 px-2 text-xs uppercase tracking-wider text-gray-500 font-semibold">Ver senha</th>
                        <th className="text-center py-2 px-2 text-xs uppercase tracking-wider text-gray-500 font-semibold">Editar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((u) => {
                        const p = permMap[u.id];
                        return (
                          <tr key={u.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`perm-row-${u.id}`}>
                            <td className="py-2 px-3">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: "#6B7280" }}>
                                  {u.name.slice(0,1).toUpperCase()}
                                </div>
                                <div>
                                  <div className="font-medium text-[#1F1F24] text-sm">{u.name}</div>
                                  <div className="text-xs text-gray-500">{u.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="py-2 px-2 text-center">
                              <Checkbox
                                checked={!!p?.can_view}
                                onCheckedChange={() => togglePermission(u.id, "can_view")}
                                data-testid={`perm-view-${u.id}`}
                              />
                            </td>
                            <td className="py-2 px-2 text-center">
                              <Checkbox
                                checked={!!p?.can_view_secret}
                                onCheckedChange={() => togglePermission(u.id, "can_view_secret")}
                                data-testid={`perm-view-secret-${u.id}`}
                              />
                            </td>
                            <td className="py-2 px-2 text-center">
                              <Checkbox
                                checked={!!p?.can_edit}
                                onCheckedChange={() => togglePermission(u.id, "can_edit")}
                                data-testid={`perm-edit-${u.id}`}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="text-xs text-gray-500 flex items-center gap-1.5">
                <User className="w-3 h-3" /> {form.permissions.length} permissão(ões) individual(is) configurada(s).
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="gap-2 pt-2 border-t border-gray-100 mt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="cred-form-cancel">Cancelar</Button>
            <Button type="submit" disabled={saving} className="vp-btn-primary text-white" data-testid="cred-form-submit">
              {saving ? "Salvando..." : editing ? "Atualizar" : "Criar credencial"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
